import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Search, Mail, RefreshCw, FileBarChart, ExternalLink, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin Reports — searchable grid of historical weekly snapshots.
 * Each row drills into a modal that mirrors the analytics tiles + funnel breakdown.
 */
export default function AdminReports({ onRunNow }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: q ? { search: q } : {} });
      setReports(data.reports || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  return (
    <section className="space-y-4" data-testid="admin-reports-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">Historical Reports</div>
          <h2 className="font-display text-lg text-white tracking-tight">Weekly snapshots archive</h2>
        </div>
        <div className="flex items-center gap-2">
          <form onSubmit={onSearch} className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/40" />
              <Input
                data-testid="reports-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by id or subject…"
                className="pl-8 h-9 bg-charcoal border-border rounded-sm w-[240px]"
              />
            </div>
            <Button data-testid="reports-search-btn" type="submit" size="sm" variant="outline" className="border-border bg-charcoal rounded-sm">
              Search
            </Button>
          </form>
          <Button
            data-testid="reports-refresh-btn"
            size="sm"
            variant="outline"
            onClick={() => load(search)}
            className="border-border bg-charcoal rounded-sm"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {reports.length === 0 && !loading && (
        <div className="sr-card p-10 text-center" data-testid="reports-empty">
          <FileBarChart className="h-7 w-7 mx-auto text-white/30 mb-3" />
          <p className="text-white/55 text-sm">No reports yet. Trigger one now to seed the archive.</p>
          {onRunNow && (
            <Button onClick={onRunNow} className="mt-5 bg-teal hover:bg-teal-hover text-white rounded-sm" size="sm">
              <Mail className="h-3.5 w-3.5 mr-2" /> Run Weekly Report
            </Button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="reports-grid">
        {reports.map((r) => {
          const sent = r.email_send_result?.sent === true;
          const d = r.data || {};
          return (
            <button
              key={r.report_id}
              data-testid={`report-card-${r.report_id}`}
              onClick={() => setSelected(r)}
              className="sr-card p-5 text-left hover:border-teal/40 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
                  {new Date(r.generated_at).toLocaleDateString()}
                </span>
                <Badge
                  variant="outline"
                  className={`rounded-sm font-mono text-[9px] tracking-wider ${
                    sent
                      ? "bg-teal/10 text-teal border-teal/40"
                      : "bg-white/5 text-white/50 border-white/15"
                  }`}
                >
                  {sent ? <><Check className="h-2.5 w-2.5 mr-1" />SENT</> : <><X className="h-2.5 w-2.5 mr-1" />NOT SENT</>}
                </Badge>
              </div>
              <div className="mt-3 text-white text-sm font-medium truncate">{r.subject}</div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] font-mono">
                <div>
                  <div className="text-white/40 uppercase tracking-[0.14em] text-[9px]">Users</div>
                  <div className="text-white text-base font-display font-black mt-0.5">{d.total_users ?? "—"}</div>
                </div>
                <div>
                  <div className="text-white/40 uppercase tracking-[0.14em] text-[9px]">7d new</div>
                  <div className="text-white text-base font-display font-black mt-0.5">{d.new_signups_7d ?? "—"}</div>
                </div>
                <div>
                  <div className="text-white/40 uppercase tracking-[0.14em] text-[9px]">MRR</div>
                  <div className="text-teal text-base font-display font-black mt-0.5">${d.mrr?.toFixed?.(2) ?? "0.00"}</div>
                </div>
              </div>
              <div className="mt-3 text-[10px] font-mono text-white/30 truncate group-hover:text-teal flex items-center gap-1">
                {r.report_id} <ExternalLink className="h-2.5 w-2.5" />
              </div>
            </button>
          );
        })}
      </div>

      <ReportDetailModal report={selected} onClose={() => setSelected(null)} />
    </section>
  );
}

function ReportDetailModal({ report, onClose }) {
  if (!report) return null;
  const d = report.data || {};
  const tiles = [
    { k: "Total Users", v: d.total_users ?? 0 },
    { k: "New / 7d", v: d.new_signups_7d ?? 0 },
    { k: "Active Trials", v: d.active_trials ?? 0 },
    { k: "Conversions", v: d.total_conversions ?? 0 },
    { k: "Generations", v: d.total_generations ?? 0 },
    { k: "MRR", v: `$${(d.mrr ?? 0).toFixed(2)}` },
  ];
  const states = d.state_counts || {};
  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="report-detail-modal" className="max-w-2xl bg-charcoal border-border">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-white">{report.subject}</DialogTitle>
          <DialogDescription className="text-white/50 font-mono text-[11px] tracking-[0.12em] uppercase">
            {report.report_id} · Generated {new Date(report.generated_at).toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 grid grid-cols-3 gap-2">
          {tiles.map((t) => (
            <div key={t.k} className="bg-black/30 border border-border p-3 rounded-sm">
              <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-white/40">{t.k}</div>
              <div className="mt-1 text-xl font-display font-black text-white">{t.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40 mb-2">Funnel State Breakdown</div>
          <div className="grid grid-cols-2 gap-1.5 text-sm">
            {Object.entries(states).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-t border-border py-1.5 font-mono">
                <span className="text-white/60 capitalize text-[12px]">{k.replace(/_/g, " ")}</span>
                <span className="text-white text-[12px]">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-white/40">
          Email delivery: {report.email_send_result?.sent ? <span className="text-teal">SENT</span> : <span className="text-white/60">NOT DELIVERED — {(report.email_send_result?.error || report.email_send_result?.reason || "").slice(0, 60)}</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
